import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesService } from '../services/roles.service';
import type { CreateRoleDto, UpdateRoleDto } from '../types/role.types';

const ROLES_KEY = 'opportunity-roles';

/**
 * Hook pour récupérer les rôles d'une opportunité
 */
export function useOpportunityRoles(opportunityId: number | undefined) {
  return useQuery({
    queryKey: [ROLES_KEY, opportunityId],
    queryFn: () => rolesService.getByOpportunity(opportunityId!),
    enabled: !!opportunityId,
  });
}

/**
 * Hook pour récupérer le coordinateur d'une opportunité
 */
export function useOpportunityCoordinator(opportunityId: number | undefined) {
  return useQuery({
    queryKey: [ROLES_KEY, opportunityId, 'coordinator'],
    queryFn: () => rolesService.getCoordinator(opportunityId!),
    enabled: !!opportunityId,
  });
}

/**
 * Hook pour créer une assignation de rôle
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateRoleDto) => rolesService.create(dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ROLES_KEY, variables.opportunityId],
      });
    },
  });
}

/**
 * Hook pour mettre à jour une assignation de rôle
 */
export function useUpdateRole(opportunityId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateRoleDto }) =>
      rolesService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_KEY, opportunityId] });
    },
  });
}

/**
 * Hook pour supprimer une assignation de rôle
 */
export function useDeleteRole(opportunityId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => rolesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLES_KEY, opportunityId] });
    },
  });
}
